import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useNotif from 'src/hooks/useNotif';
import { setLimits } from 'src/redux/slices/storedValues';
import { useRequest, useToggle } from 'src/hooks';
import { setup, trans } from 'src/lib/endpoints';
import { object } from 'prop-types';

export default function useSubSearch({
  ixSubType,
  sSubType,
  disregardLimits,
  autoSelect,
  selectSub,
  isOpen,
  showEdit,
  showAdd,
  transDetails = {},
  isTax = false,
  ...rest
}) {
  const {
    filterByParent,
    ixSubParent,
    ixSubParentSource,
    ixFilter,
    subFilter
  } = rest || {};
  const lastFetchQueryRef = useRef('');
  const didMount = useRef(false);

  const dispatch = useDispatch();
  const notify = useNotif();
  const { current_user } = useSelector(state => state.auth);
  const { tax } = useSelector(({ bizMeta }) => bizMeta);
  const { limits: subLimits } = useSelector(state => state.storedValues);
  const [loading, loadingOn, loadingOff] = useToggle();
  const [error, hasError, clearError] = useToggle();
  const [isFormOpen, openForm, closeForm] = useToggle(false);
  const [showInactive, , , toggleInactive] = useToggle();

  const [subList, setSubList] = useState([]);
  const [parentSubList, setParentSubList] = useState([]);
  const [meta, setMeta] = useState({});
  const [subAccDetails, setSubAccDetails] = useState({});
  const [query, setQuery] = useState('');

  const limits = subLimits?.[current_user?.username]?.[ixSubType]?.limits || [];
  const hasOnlyOneOption = limits.length === 1;
  const hasStoredLimits = Boolean(
    subLimits?.[current_user.username]?.[ixSubType]
  );

  const request = useRequest(loadingOn, loadingOff);
  const request2 = useRequest();

  const filterSearch = (result = []) => {
    if (limits.length === 0 || disregardLimits) return result;

    return result.filter(subAcc =>
      limits.some(limit => limit.ixSub === subAcc.ixSub)
    );
  };

  const readValue = v => {
    if (v == null) return '';
    if (typeof v === 'string' || typeof v === 'number') return String(v);
    if (Array.isArray(v)) return v.map(readValue).join(' ');
    if (typeof v === 'object') return v.sSub || v.sub_title || v.name || '';
    return '';
  };

  const includesQuery = (item, q) => {
    const s = (q || '')
      .toString()
      .trim()
      .toLowerCase();
    if (s === '') return true;
    const fields = [
      item.sub_title,
      item.sSub,
      item.sub_id,
      item.RefNo,
      item.sSubParent,
      item.sSubLink1,
      item.sSubLink2,
      item.sSubLink3,
      item.sSubLink4,
      item.sSubLink5
    ];

    return fields.some(v =>
      readValue(v)
        .toLowerCase()
        .includes(s)
    );
  };

  const fetchSubList = async ({ q, type = '' }) => {
    loadingOn();
    let cancel = () => {};
    const currentQ = q || '';
    clearError();

    const lstSubParentPayload = transDetails?.[ixSubParentSource]?.value
      ? { lstSubParent: [transDetails[ixSubParentSource].value] }
      : {};

    const res = await request2.post(
      setup.sub.list,
      {
        ixSubType,
        q,
        showInactive,
        ...lstSubParentPayload,
        ...(isTax ? { tax: true } : {})
      },
      c => {
        cancel = c;
      }
    );

    if (lastFetchQueryRef.current !== currentQ && type == 'query') {
      return cancel; // don't overwrite state
    }

    if (res.success) {
      let next = res.data?.sub_list || [];

      if (subFilter && ixFilter) {
        next = next.filter(item => item[subFilter] === ixFilter);
      }

      setSubList(prev => {
        if (
          prev.length === next.length &&
          prev.every((p, i) => p.sub_id === next[i]?.sub_id)
        ) {
          return prev; // arrays "equal" → no update
        }
        return next; // arrays differ → update
      });
      // setSubList(res.data?.sub_list || []);
    } else if (!res.isCancelled) {
      hasError();
    }
    loadingOff();
    return cancel;
  };

  const loadLimits = async () => {
    if (!hasStoredLimits && ixSubType) {
      const limit = await request.get(trans.limits(ixSubType));

      if (limit.success) {
        dispatch(
          setLimits({
            ixSubType,
            limits: limit.data,
            sSubType
          })
        );

        if (autoSelect) {
          const defaultSub = limit.data.find(sub => Boolean(sub.bDefault));
          if (defaultSub)
            selectSub({
              ixSub: defaultSub.ixSub,
              sub_id: defaultSub.ixSub,
              sSub: defaultSub.sub_title,
              sub_title: defaultSub.sub_title,
              ...rest
            });
          else if (limit.data.length === 1) {
            const sub = limit.data[0];
            selectSub({
              ixSub: sub.ixSub,
              sub_id: sub.ixSub,
              sSub: sub.sub_title,
              sub_title: sub.sub_title,
              ...rest
            });
          }
        }
      }
    }
  };

  function createSubAcc() {
    setSubAccDetails({});
    openForm();
  }

  async function editSubAcc(ixSub) {
    const res = await request.get(setup.sub.subDetails(ixSub));

    if (res.success) {
      openForm();
      setSubAccDetails(res.data);
    } else notify.error(res.errorMessage);
  }

  useEffect(() => {
    if (!filterByParent) return;

    let cancel = () => {};
    clearError();

    (async function() {
      const res = await request.post(
        setup.sub.list,
        {
          ixSubType: ixSubParent,
          q: (query || '').trim(),
          showInactive
        },
        function(c) {
          cancel = c;
        }
      );

      if (res.success) setParentSubList(res.data?.sub_list || []);
      else if (!res.isCancelled) hasError();
    })();

    return () => cancel();
  }, [filterByParent]);

  // useEffect(() => {
  //   if (!hasStoredLimits) {
  //     return;
  //   }

  //   let cancel = () => {};
  //   clearError();

  //   (async function() {
  //     const lstSubParentPayload = transDetails?.[ixSubParentSource]?.value
  //       ? { lstSubParent: [transDetails[ixSubParentSource].value] }
  //       : {};

  //     // const q = (query || '').trim();

  //     const res = await request.post(
  //       setup.sub.list,
  //       {
  //         ixSubType,
  //         q: (query || '').trim(),
  //         showInactive,
  //         ...lstSubParentPayload,
  //         ...(isTax ? { tax: true } : {})
  //         //tax:true
  //       },
  //       function(c) {
  //         cancel = c;
  //       }
  //     );
  //     // TODO: Add option/button to search by parent

  //     //    if (res.success) {
  //     //   const primary = res.data?.sub_list || [];
  //     //   if (q !== '') {
  //     //     const resAll = await request.post(setup.sub.list, {
  //     //       ixSubType,
  //     //       q: '',
  //     //       showInactive,
  //     //       ...lstSubParentPayload,
  //     //       ...(isTax ? { tax: true } : {})
  //     //     });
  //     //     if (resAll?.success) {
  //     //       const allList = resAll.data?.sub_list || [];
  //     //       const merged = new Map();

  //     //       [...primary, ...allList.filter(it => includesQuery(it, q))].forEach(
  //     //         it => {
  //     //           if (it?.sub_id != null) merged.set(it.sub_id, it);
  //     //         }
  //     //       );

  //     //       setSubList([...merged.values()]);
  //     //     } else {
  //     //       setSubList(primary);
  //     //     }
  //     //   } else {
  //     //     setSubList(primary);
  //     //   }
  //     // } else if (!res.isCancelled) hasError();

  //     if (res.success) setSubList(res.data?.sub_list || []);
  //     else if (!res.isCancelled) hasError();
  //   })();

  //   return () => cancel();
  // }, [
  // query,
  // isOpen,
  // hasStoredLimits,
  // showInactive,
  // transDetails?.[ixSubParentSource]?.value
  // ]);

  // query changes
  useEffect(() => {
    if (!hasStoredLimits) return;

    const q = (query || '').trim();

    // optional guards
    if (q === lastFetchQueryRef.current) return;
    lastFetchQueryRef.current = q;

    let cancel = () => {};

    const timeout = setTimeout(async () => {
      cancel = await fetchSubList({ q, type: 'query' });
    }, 500);

    return () => {
      clearTimeout(timeout);
      cancel();
    };
  }, [query]);

  // for non query dependencies
  useEffect(() => {
    if (!hasStoredLimits) return;
    if (didMount.current == false) {
      didMount.current = true;
      return;
    }
    const q = (query || '').trim();

    lastFetchQueryRef.current = q;
    let cancel = () => {};

    (async () => {
      cancel = await fetchSubList({ q: q });
    })();

    return () => cancel();
  }, [
    hasStoredLimits,
    showInactive,
    transDetails?.[ixSubParentSource]?.value,
    ixSubType
  ]);

  // specifically for first open
  useEffect(() => {
    if (!isOpen || !hasStoredLimits) return;
    if (subList?.length) return; // already loaded

    let cancel = () => {};

    const q = (query || '').trim();

    lastFetchQueryRef.current = q;

    (async () => {
      cancel = await fetchSubList({ q: q ?? '' });
    })();

    return () => cancel();
  }, [isOpen, hasStoredLimits]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    loadLimits();
  }, [hasStoredLimits, ixSubType, isOpen]);

  useEffect(() => {
    if ((!showAdd && showEdit) || !ixSubType || !isFormOpen) {
      return;
    }

    let cancel = () => {};

    (async function() {
      const meta = await request.get(setup.subType.meta(ixSubType));

      if (meta.success) {
        // Use meta endpoint
        setMeta(meta.data || {});
      } else notify.error(meta.error);
    })();

    return () => {
      cancel();
    };
  }, [showAdd, showEdit, ixSubType, isFormOpen]);

  return {
    loading,
    loadingOn,
    loadingOff,
    error,
    subList: filterSearch(subList),
    meta,
    isFormOpen,
    openForm,
    closeForm,
    subAccDetails,
    limits,
    hasOnlyOneOption,
    query,
    setQuery,
    editSubAcc,
    createSubAcc,
    showInactive,
    toggleInactive,
    parentSubList
  };
}
